/**
 * Marked text — the ONE contract for every śikṣā-marked Sanskrit text in Veda
 * Union.
 *
 * A "marked document" is a title + sections + verses whose text is a stream of
 * TOKENS: syllables built from letter-level units carrying holdings / svara /
 * anusvāra-visarga change colour, plus spaces, pauses, daṇḍas, verse numbers,
 * free-text fills and variable slots. Every syllable carries its form in all
 * four scripts, so the reader is script-agnostic.
 *
 * Three kinds of source satisfy this contract, and the reader renders all three
 * identically:
 *
 *   1. a **static chant JSON** (`client/public/chants/*.json`, generated offline
 *      by `tools/chant/`) — the whole document;
 *   2. a **slice** of one (`ChantSelection` — sections, verses, or a range),
 *      so a document can embed just the Gaṇeśa stotram, or three of its verses;
 *   3. a **variable module** that composes its sections at render time — the
 *      saṅkalpa (`shared/src/sankalpa.ts`) builds a `ChantDoc` from precomputed
 *      fragment marks + the day's pañcāṅga coordinates.
 *
 * There is NO runtime marking engine anywhere. Marks are emitted offline by
 * `tools/chant/gen_marks.py` and shipped as data (see docs/MARKING-RULES.md).
 */
/** Canonical JSON — keys sorted, no spaces. Must match the generator's
 *  `json.dumps(..., sort_keys=True, separators=(",", ":"), ensure_ascii=False)`
 *  byte for byte, or `baseHash` can never validate. */
export function canonicalJson(value) {
    if (value === null || typeof value !== 'object')
        return JSON.stringify(value) ?? 'null';
    if (Array.isArray(value))
        return `[${value.map(canonicalJson).join(',')}]`;
    const o = value;
    const keys = Object.keys(o).sort();
    return `{${keys.map((k) => `${JSON.stringify(k)}:${canonicalJson(o[k])}`).join(',')}}`;
}
/** The highest format version this build renders. */
export const CHANT_FORMAT_VERSION = 3;
/* ==========================================================================
   Normalising a loaded document
   ========================================================================== */
/** Does this step stand on instructions / figures / embeds alone? */
export function isTextlessStep(s) {
    return !s.verses.length && !s.module && !!s.items?.length;
}
/**
 * Bring a loaded document to the v3 in-memory shape, so every consumer sees ONE
 * model: `items` always present and always in step with `verses`, `title`
 * always set. Pure, idempotent, and additive — a v2 document (the other three
 * chants) comes through unchanged in meaning.
 */
export function normalizeChantDoc(doc) {
    let touched = false;
    const sections = doc.sections.map((s) => {
        const verses = s.verses ?? [];
        const items = s.items
            ? s.items
            : verses.map((v) => ({ t: 'verse', ...v }));
        const title = s.title ?? s.label ?? '';
        // `items` is authoritative when authored; keep `verses` derived from it so
        // audio lookup, deep links and the practice cursor need no new code path.
        const fromItems = s.items
            ? s.items.filter((it) => it.t === 'verse')
                .map(({ t: _t, ...v }) => v)
            : verses;
        if (s.items || !s.title || s.verses !== fromItems)
            touched = true;
        return { ...s, title, items, verses: fromItems };
    });
    if (!touched)
        return doc;
    return { ...doc, sections };
}
/* ==========================================================================
   `select` grammar for an embed (docs/DOCUMENT-COMPOSITION.md §4.3)

     #<sectionId>                                    one section
     #<sectionId>/<verseId>                          one verse
     #<sec>/<verse>..#<sec>/<verse>                  inclusive verse range
     #<sec>..#<sec>                                  inclusive section range
     (absent)                                        the whole document

   Ids are the TARGET's own — the same ids the deep-link fragment uses. No
   indices: an index breaks the moment the target gains a verse, and this format
   is regenerated often.
   ========================================================================== */
/** Parse a `select` string into a `ChantSelection`. Returns null when the string
 *  is malformed (the caller reports it as an authoring error, cause `anchor`). */
export function parseChantSelect(select) {
    if (!select || !select.trim())
        return {};
    const parts = select.trim().split('..');
    if (parts.length > 2)
        return null;
    const one = (raw) => {
        const m = /^#([^/\s]+)(?:\/([^/\s]+))?$/.exec(raw.trim());
        if (!m)
            return null;
        return { section: m[1], verse: m[2] };
    };
    const a = one(parts[0]);
    if (!a)
        return null;
    if (parts.length === 1) {
        return a.verse ? { sections: [a.section], verses: [a.verse] } : { sections: [a.section] };
    }
    const b = one(parts[1]);
    if (!b)
        return null;
    if (!!a.verse !== !!b.verse)
        return null;
    if (a.verse && b.verse)
        return { from: a.verse, to: b.verse };
    return { sectionFrom: a.section, sectionTo: b.section };
}
export function isEmptySelection(sel) {
    if (!sel)
        return true;
    return (!sel.sections?.length && !sel.exclude?.length && !sel.verses?.length &&
        !sel.from && !sel.to && !sel.sectionFrom && !sel.sectionTo);
}
/** Apply a selection. Pure; returns the same object when nothing is selected. */
export function sliceChantDoc(doc, sel) {
    if (isEmptySelection(sel) || !sel)
        return doc;
    // A step's `items` array is authoritative, so every verse filter has to cut
    // BOTH lists or the two drift apart (and a dropped verse would still render
    // out of `items`). One helper, used by every branch below.
    const keepVerses = (s, keep) => ({
        ...s,
        verses: s.verses.filter((v) => keep.has(v.id)),
        ...(s.items
            ? { items: s.items.filter((it) => it.t !== 'verse' || keep.has(it.id)) }
            : {}),
    });
    let sections = doc.sections;
    if (sel.sections?.length) {
        const keep = new Set(sel.sections);
        sections = sections.filter((s) => keep.has(s.id));
    }
    if (sel.exclude?.length) {
        const drop = new Set(sel.exclude);
        sections = sections.filter((s) => !drop.has(s.id));
    }
    if (sel.sectionFrom || sel.sectionTo) {
        const order = sections.map((s) => s.id);
        const start = sel.sectionFrom ? order.indexOf(sel.sectionFrom) : 0;
        const end = sel.sectionTo ? order.indexOf(sel.sectionTo) : order.length - 1;
        if (start >= 0 && end >= 0 && end >= start)
            sections = sections.slice(start, end + 1);
    }
    if (sel.verses?.length) {
        const keep = new Set(sel.verses);
        sections = sections.map((s) => keepVerses(s, keep));
    }
    if (sel.from || sel.to) {
        const order = sections.flatMap((s) => s.verses.map((v) => v.id));
        const start = sel.from ? order.indexOf(sel.from) : 0;
        const end = sel.to ? order.indexOf(sel.to) : order.length - 1;
        if (start >= 0 && end >= 0 && end >= start) {
            const keep = new Set(order.slice(start, end + 1));
            sections = sections.map((s) => keepVerses(s, keep));
        }
    }
    // Keep a step that has text left, a composed module, or that never had any
    // mantra to begin with (prāṇāyāma stands on its instructions alone).
    sections = sections.filter((s) => s.verses.length > 0 || !!s.module || isTextlessStep(s));
    // A selection that matched nothing is a bad id, not an instruction to render
    // an empty page: fall back to the whole document rather than a blank card.
    if (!sections.length)
        return doc;
    return { ...doc, sections };
}
/** Substitute variable slots. Pure. `slots` maps a slot name to its tokens. */
export function fillChantSlots(tokens, slots) {
    if (!tokens.some((t) => t.t === 'slot'))
        return tokens;
    const out = [];
    for (const tk of tokens) {
        if (tk.t !== 'slot') {
            out.push(tk);
            continue;
        }
        const replacement = slots?.[tk.name];
        out.push(...(replacement && replacement.length ? replacement : tk.tokens));
    }
    return out;
}
